import { RefObject, useEffect, useState } from "react";
import { styled } from "styled-components";

import { BookIcon, PaperAirplaneIcon } from "@vericus/cadmus-icons";
import { useManualPreset } from "@vericus/cadmus-manual";
import { Alert, Button, Spacer } from "@vericus/cadmus-ui";

import { isAfter } from "date-fns";

import { FocusTitle } from "@/components/focus-title";
import { SubmissionDeclaration } from "@/components/submission-declaration";
import { AuthorityState } from "@/features/authority";
import { SubmissionType } from "@/generated/graphql";
import { useGenaiGuidelineNotice } from "@/graphql/hooks";
import { ManualWithMutations } from "@/ui/shared/ManualWithMutations";
import { isLate } from "@/utils/datetime";

import {
  createAllowedSubmissions,
  SubmissionOption,
} from "./createAllowedSubmissions";
import { BothOption, NoOption, OneOption } from "./Options";
import { Timeline } from "./Timeline";

interface SubmitActionProps {
  /** The work ID for localStorage acceptance tracking. */
  workId: string;
  /** Whether the student can re submit a final after the due date. */
  allowLateResubmissions: boolean;
  /** Whether the student has reached the maximum number of allowed submission attempts. */
  submitAttemptLimitReached: boolean;
  /** Datetime to be used as submission timestamp */
  submissionDate: Date;
  /** Existing draft submission? */
  hasDraft: boolean;
  /** Existing final submission? */
  hasFinal: boolean;
  /** Instruction Sheet release Date */
  releaseDate: Date | null;
  /** Final due date */
  finalDueDate: Date | null;
  /** Draft due date */
  draftDueDate: Date | null;
  /* Work start date for a time limited assessment */
  startDate: Date | null;
  /** Instructions time limit for a time limited assessment */
  endDate: Date | null;
  /** Whether Gen AI declaration is required for this assessment */
  requireGenAiDeclaration: boolean;
  /** Whether the submission declarations have been accepted */
  hasAcceptedSubmissionDeclarations: AuthorityState["hasAcceptedSubmissionDeclarations"];
  /** Callback to cancel the submission */
  onCancel: () => void;
  /** Callback to confirm and make the submission */
  onSubmit: (t: SubmissionType) => void;
  /** Disable the submission with a message */
  disabledMessage?: string;
  /** Callback for when the student accepts or rejects a submission declaration. */
  onUpdateSubmissionDeclaration: (
    submissionDeclaration: keyof AuthorityState["hasAcceptedSubmissionDeclarations"],
    hasAccepted: boolean
  ) => void;
  /** Whether the Submission was triggered automatically at the time limit */
  isTimeLimitTriggered?: boolean;
  /** Preview page manual mount point */
  manualMount: RefObject<HTMLDivElement>;
}

/**
 * Displays submission options for student to choose from.
 *
 * There are 3 main scenarios
 *  1. 2 Options - draft and final can be submitted.
 *                    student get to choose which submission to make.
 *  2. 1 option - only one type of submission can be submitted.
 *                    student can only submit the allowed submission type.
 *  3. 0 option - no submissions are allowed. Student can't submit.
 *
 *
 * Also displays user declaration that must be checked before submission.
 */
export function SubmitAction({
  workId,
  allowLateResubmissions,
  submitAttemptLimitReached,
  submissionDate,
  hasDraft,
  hasFinal,
  releaseDate,
  finalDueDate,
  draftDueDate,
  startDate,
  endDate,
  disabledMessage: parentDisabledMessage,
  requireGenAiDeclaration,
  hasAcceptedSubmissionDeclarations,
  onUpdateSubmissionDeclaration,
  onCancel,
  onSubmit,
  isTimeLimitTriggered,
  manualMount,
}: SubmitActionProps) {
  // Selected submission type from the currently allowed types of submissions
  const lateDraft = draftDueDate ? isLate(draftDueDate, submissionDate) : false;
  const lateFinal = finalDueDate ? isLate(finalDueDate, submissionDate) : false;
  const overtime = endDate !== null ? isAfter(submissionDate, endDate) : null;

  const genaiGuidelineNotice = useGenaiGuidelineNotice(workId);

  const openManualPreset = useManualPreset();

  // Current allowed type of submission and its meta description
  const [allowed, bestOption] = createAllowedSubmissions({
    allowLateResubmissions,
    hasDraftDue: !!draftDueDate,
    hasDueDate: !!finalDueDate,
    hasDraft,
    hasFinal,
    lateDraft,
    lateFinal,
    overtime: overtime === true,
    onTimeLimit: isTimeLimitTriggered === true,
  });

  const [submissionOption, setSubmissionOption] =
    useState<SubmissionOption | null>(bestOption);

  useEffect(() => {
    setSubmissionOption(bestOption);
  }, [bestOption]);

  const declarationsNotAccepted =
    !hasAcceptedSubmissionDeclarations.academicIntegrity ||
    (requireGenAiDeclaration &&
      !hasAcceptedSubmissionDeclarations.generativeAi);

  const disabledMessage =
    parentDisabledMessage ??
    (submitAttemptLimitReached
      ? "You have reached the maximum number of allowed submission attempts."
      : !submissionOption
        ? "Sorry, submission isn't available at the moment"
        : undefined);

  const submitDisabled =
    declarationsNotAccepted || disabledMessage !== undefined;

  // Components depending on number of submission options
  let title;
  let option;

  switch (allowed.option) {
    case "none":
      title = allowed.title;
      option = <NoOption subtext={allowed.subtext} />;
      break;
    case "one":
      title = allowed.submission.text;
      option = <OneOption submission={allowed.submission} />;
      break;
    case "both":
      title = "Choose a submission";
      option = (
        <BothOption
          draft={allowed.draft}
          final={allowed.final}
          submission={submissionOption}
          setSubmission={setSubmissionOption}
        />
      );
      break;
  }

  return (
    <Container>
      <div>
        <ManualWithMutations
          mountElement={manualMount}
          trigger={
            <div style={{ position: "absolute", top: 12, right: 12 }}>
              <Button
                kind="ghost"
                size="sm"
                aria-label="Open Cadmus Manual"
                icon={<BookIcon size={24} label="" />}
              />
            </div>
          }
        />

        <Spacer spacing={20} />
        <FocusTitle kind="displayTwo">{title}</FocusTitle>
        <Spacer spacing={16} />
        {releaseDate && finalDueDate && (
          <Timeline
            submissionDate={submissionDate}
            releasedDate={releaseDate}
            draftDue={draftDueDate}
            dueDate={finalDueDate}
            draft={draftDueDate ? hasDraft : undefined}
            final={hasFinal}
            startDate={startDate}
            endDate={endDate}
          />
        )}
        {option}
        <Spacer spacing={16} />
      </div>
      <div>
        <SubmissionDeclaration
          acceptanceScopeId={workId}
          requireGenAiAgreement={requireGenAiDeclaration}
          onUpdateSubmissionDeclaration={onUpdateSubmissionDeclaration}
          hasCheckedPledges={hasAcceptedSubmissionDeclarations}
          genaiGuidelineNotice={genaiGuidelineNotice}
          onOpenManualPreset={openManualPreset}
        />
        <Button onClick={onCancel} style={{ marginRight: 18, marginBottom: 8 }}>
          Cancel
        </Button>
        {allowed.option !== "none" && (
          <>
            <Button
              kind="secondary"
              rightIcon={<PaperAirplaneIcon size={24} label="send" />}
              disabled={submitDisabled}
              onClick={() =>
                submissionOption && onSubmit(submissionOption.type)
              }
            >
              {submissionOption?.text ?? "Submit"}
            </Button>
            {submitDisabled && disabledMessage !== undefined && (
              <>
                <Spacer spacing={24} />
                <Alert size="sm">{disabledMessage}</Alert>
              </>
            )}
          </>
        )}
        <Spacer spacing={32} />
      </div>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  height: 100%;
  flex-direction: column;
  justify-content: space-between;

  width: 100%;
  min-width: 320px;
  max-width: min(800px, 100%);
  padding: 0 45px;
  margin: auto;
  box-sizing: border-box;
  text-align: center;
`;
